import React, { useMemo, useState, createContext, useContext } from 'react';
const SHOPIFY_CART_URL = 'https://savia-9476.myshopify.com/cart';
export type CartItem = {
  id: string;
  variantId: number;
  name: string;
  aroma: string;
  format: string;
  price: number;
  image?: string;
  quantity: number;
};
type AddCartItem = Omit<CartItem, 'id' | 'quantity'>;
type CartContextValue = {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  addItem: (item: AddCartItem) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  isCheckingOut: boolean;
  checkoutError: string | null;
  clearCheckoutError: () => void;
  checkout: () => Promise<void>;
};
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const addItem = (item: AddCartItem) => {
    const id = createItemId(item);
    setItems(current => {
      const existing = current.find(cartItem => cartItem.id === id);
      if (!existing) {
        return [...current, {
          ...item,
          id,
          quantity: 1
        }];
      }
      return current.map(cartItem => cartItem.id === id ? {
        ...cartItem,
        quantity: cartItem.quantity + 1
      } : cartItem);
    });
  };
  const updateQuantity = (id: string, quantity: number) => {
    setItems(current => quantity < 1 ? current.filter(item => item.id !== id) : current.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  };
  const removeItem = (id: string) => {
    setItems(current => current.filter(item => item.id !== id));
  };
  const checkout = async () => {
    if (!items.length || isCheckingOut) return;
    setIsCheckingOut(true);
    setCheckoutError(null);
    try {
      const cartPath = items.map(item => `${item.variantId}:${item.quantity}`).join(',');
      window.location.assign(`${SHOPIFY_CART_URL}/${cartPath}`);
    } catch {
      setCheckoutError('No fue posible iniciar el checkout. Inténtalo de nuevo.');
    } finally {
      setIsCheckingOut(false);
    }
  };
  const value = useMemo(() => ({
    items,
    itemCount,
    subtotal,
    addItem,
    updateQuantity,
    removeItem,
    isCheckingOut,
    checkoutError,
    clearCheckoutError: () => setCheckoutError(null),
    checkout
  }), [items, itemCount, subtotal, isCheckingOut, checkoutError]);
  return <CartContext.Provider value={value} data-id="element-21">{children}</CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart debe usarse dentro de CartProvider.');
  return context;
}
function createItemId(item: AddCartItem) {
  return String(item.variantId);
}